import { useMemo } from 'react';
import {
  getActivePipeexProfile,
  type SettingInfoDto,
  type SettingsType,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { buildWorkflowResumeScopeKey } from '../../../shared/stores/useWorkflowResumeStore';
import {
  normalizeMappingProjectKey,
  readMappingProfileProjectTrust,
} from '../lib/mappingProfileProjectTrust';
import {
  buildSettingInfoProfileKey,
  isE2eMappingBypassActive,
} from '../lib/mappingPageProjectProfileUtils';
import {
  canAutoLoadProjectProfileDecision,
  resolveRememberedProjectBindingDecision,
  resolveProjectProfileGateDecision,
  type ProjectProfileGateDecision,
} from '../lib/projectProfileGateDecision';
import {
  isMappingProfileApplyInFlight,
  type MappingProfileApplyProgressState,
} from './profileControllerShared';
import type {
  ProjectProfileDocumentInfo,
  ProjectProfileManualSelection,
  ProjectProfilePreferredProfile,
  ProjectSessionProfileConfirmationSource,
} from './projectProfileGateContracts';
import { useMappingProfileProjectMatches } from './useMappingProfileProjectMatches';

interface UseProjectProfileGateEvaluationArgs {
  readonly activeUserId?: string | null;
  readonly activeDocumentInfo: ProjectProfileDocumentInfo;
  readonly selectedProfileKey: string;
  readonly selectedSettingsType: SettingsType;
  readonly lastAppliedProfileKey: string;
  readonly profileApplyProgress: MappingProfileApplyProgressState | null;
  readonly profiles: SettingInfoDto[];
  readonly profilesLoading: boolean;
  readonly projectPreferredProfile: ProjectProfilePreferredProfile | null;
  readonly projectIdentityPending: boolean;
  readonly dockablePaneVisible: boolean;
  readonly isLiveWizardMappingHandoff: boolean;
  readonly profileTrustRevision: number;
  readonly projectSessionConfirmedProfileKey: string | null;
  readonly projectSessionProfileConfirmationSource: ProjectSessionProfileConfirmationSource | null;
  readonly manualProjectProfileSelection: ProjectProfileManualSelection | null;
}

export function useProjectProfileGateEvaluation({
  activeUserId = null,
  activeDocumentInfo,
  selectedProfileKey,
  selectedSettingsType,
  lastAppliedProfileKey,
  profileApplyProgress,
  profiles,
  profilesLoading,
  projectPreferredProfile,
  projectIdentityPending,
  dockablePaneVisible,
  isLiveWizardMappingHandoff,
  profileTrustRevision,
  projectSessionConfirmedProfileKey,
  projectSessionProfileConfirmationSource,
  manualProjectProfileSelection,
}: UseProjectProfileGateEvaluationArgs) {
  const initialActiveProfile = useMemo(() => getActivePipeexProfile(), []);
  const activeProjectSessionKey = useMemo(
    () => normalizeMappingProjectKey(activeDocumentInfo.projectKey),
    [activeDocumentInfo.projectKey]
  );
  const e2eMappingBypassActive = useMemo(() => isE2eMappingBypassActive(), []);
  const hasResolvedSelectedProfile = selectedProfileKey.trim() !== '';
  const resolvedProfileKey = hasResolvedSelectedProfile
    ? selectedProfileKey
    : activeProjectSessionKey
      ? ''
      : `${initialActiveProfile.name}::${initialActiveProfile.settingsType}`;
  const resolvedSettingsType = hasResolvedSelectedProfile
    ? selectedSettingsType
    : activeProjectSessionKey
      ? selectedSettingsType
      : initialActiveProfile.settingsType;
  const resolvedProfileName = useMemo(() => {
    const [profileName] = resolvedProfileKey.split('::');
    return profileName || 'Select profile';
  }, [resolvedProfileKey]);
  const projectProfileTrust = useMemo(() => {
    void profileTrustRevision;
    return readMappingProfileProjectTrust(
      activeUserId,
      activeDocumentInfo.projectKey,
      selectedProfileKey
    );
  }, [activeDocumentInfo.projectKey, activeUserId, profileTrustRevision, selectedProfileKey]);
  const restorePersistedWorkspace =
    !activeProjectSessionKey || projectSessionProfileConfirmationSource === null;
  const trustedProfileAppliedForCurrentSession =
    Boolean(projectProfileTrust) && lastAppliedProfileKey === selectedProfileKey;
  const profileConfirmedForCurrentSession =
    Boolean(activeProjectSessionKey) &&
    hasResolvedSelectedProfile &&
    (projectSessionConfirmedProfileKey === selectedProfileKey ||
      trustedProfileAppliedForCurrentSession);
  const resumeProfileKey =
    activeProjectSessionKey && !profileConfirmedForCurrentSession ? '' : resolvedProfileKey;
  const resumeScopeKey = useMemo(
    () =>
      projectIdentityPending || !resumeProfileKey
        ? null
        : buildWorkflowResumeScopeKey(
            resolvedSettingsType,
            resumeProfileKey,
            activeDocumentInfo.projectKey
          ),
    [activeDocumentInfo.projectKey, projectIdentityPending, resumeProfileKey, resolvedSettingsType]
  );
  const projectProfileGateEvaluationNeeded =
    dockablePaneVisible &&
    !e2eMappingBypassActive &&
    !isLiveWizardMappingHandoff &&
    !projectIdentityPending &&
    Boolean(activeProjectSessionKey) &&
    !profileConfirmedForCurrentSession;
  const profileProjectMatches = useMappingProfileProjectMatches({
    enabled: false,
    profiles,
    projectKey: activeDocumentInfo.projectKey,
    modelVersion: activeDocumentInfo.modelVersion,
    preferredProfileKey: projectPreferredProfile?.profileKey ?? null,
  });
  const rememberedExactGateProfileKey = useMemo(() => {
    const profileKey = projectPreferredProfile?.profileKey ?? '';
    if (!profileKey) {
      return '';
    }

    const match = profileProjectMatches.matches.find((item) => item.profileKey === profileKey);
    const projectAvailableCount = match?.projectAvailableCount ?? match?.availableCount ?? 0;
    return match?.status === 'match' &&
      match.referenceCount > 0 &&
      projectAvailableCount === match.referenceCount
      ? profileKey
      : '';
  }, [profileProjectMatches.matches, projectPreferredProfile?.profileKey]);
  const repairPersistenceStatus = projectPreferredProfile?.repairPersistence?.status;
  const repairPersistenceRequiresConfirmation =
    Boolean(projectPreferredProfile) && repairPersistenceStatus !== 'saved';
  const projectProfileGateDecision = useMemo<ProjectProfileGateDecision>(() => {
    const rememberedBindingDecision = repairPersistenceRequiresConfirmation
      ? null
      : resolveRememberedProjectBindingDecision({
          enabled: projectProfileGateEvaluationNeeded,
          isLoading: profilesLoading,
          availableProfileKeys: profiles.map(buildSettingInfoProfileKey),
          preferredProfileKey: projectPreferredProfile?.profileKey ?? null,
          preferredProfileSource: projectPreferredProfile?.source ?? null,
        });
    if (rememberedBindingDecision) {
      return rememberedBindingDecision;
    }

    return resolveProjectProfileGateDecision({
      enabled: projectProfileGateEvaluationNeeded,
      isLoading: profilesLoading || profileProjectMatches.isLoading,
      catalogReady: profileProjectMatches.catalogReady,
      catalogSource: profileProjectMatches.catalogSource,
      catalogError: profileProjectMatches.catalogError,
      matches: profileProjectMatches.matches,
      lastLoadedProfileKey: projectPreferredProfile?.profileKey ?? null,
      trustedProfileKey: projectPreferredProfile?.profileKey ?? null,
      trustedProfileSource: projectPreferredProfile?.source ?? null,
    });
  },
    [
      profileProjectMatches.catalogError,
      profileProjectMatches.catalogReady,
      profileProjectMatches.catalogSource,
      profileProjectMatches.isLoading,
      profileProjectMatches.matches,
      profiles,
      profilesLoading,
      repairPersistenceRequiresConfirmation,
      projectPreferredProfile?.profileKey,
      projectPreferredProfile?.source,
      projectProfileGateEvaluationNeeded,
    ]
  );
  const projectProfileMatchingPending =
    projectProfileGateDecision.mode === 'checking' || profileProjectMatches.isLoading;
  const rememberedProjectBindingCanLoadBeforeMatch =
    projectProfileMatchingPending && profileProjectMatches.projectParameterCount === 0;
  const projectProfileAutoPassProfileKey = canAutoLoadProjectProfileDecision(
    projectProfileGateDecision
  )
    ? projectProfileGateDecision.profileKey
    : null;
  const projectProfileAutoPassReason = projectProfileGateEvaluationNeeded
    ? projectProfileGateDecision.mode === 'autoPass'
      ? projectProfileGateDecision.reason
      : rememberedExactGateProfileKey
        ? 'rememberedExactMatch'
        : rememberedProjectBindingCanLoadBeforeMatch && projectPreferredProfile?.profileKey
          ? 'rememberedProjectBinding'
          : projectProfileGateDecision.reason
    : 'notEvaluating';
  const projectProfileAutoPassScopeKey =
    activeProjectSessionKey && projectProfileAutoPassProfileKey
      ? `${activeProjectSessionKey}::${projectProfileAutoPassProfileKey}`
      : null;
  const manualProjectProfileSelectionActive =
    Boolean(activeProjectSessionKey) &&
    manualProjectProfileSelection?.projectKey === activeProjectSessionKey &&
    manualProjectProfileSelection.profileKey === selectedProfileKey &&
    selectedProfileKey !== projectProfileAutoPassProfileKey;
  const projectProfileAutoPassBlocked = manualProjectProfileSelectionActive;
  const profileApplySessionActive = isMappingProfileApplyInFlight(profileApplyProgress);
  const projectProfileGateOpen =
    projectProfileGateEvaluationNeeded &&
    !profileApplySessionActive &&
    !manualProjectProfileSelectionActive &&
    projectProfileGateDecision.mode !== 'checking';
  const projectProfileGateDefaultProfileKey =
    rememberedExactGateProfileKey ||
    (projectProfileGateDecision.mode === 'autoPass' ? projectProfileGateDecision.profileKey : null) ||
    (repairPersistenceRequiresConfirmation ? projectPreferredProfile?.profileKey : null) ||
    '';

  return {
    activeProjectSessionKey,
    resolvedProfileKey,
    resolvedSettingsType,
    resolvedProfileName,
    profileConfirmedForCurrentSession,
    projectProfileTrust,
    restorePersistedWorkspace,
    resumeProfileKey,
    resumeScopeKey,
    projectProfileAutoPassProfileKey,
    projectProfileAutoPassScopeKey,
    projectProfileAutoPassBlocked,
    projectProfileAutoPassReason,
    projectProfileGateOpen,
    projectProfileGateBlocksActions: projectProfileGateEvaluationNeeded,
    projectProfileMatchingPending,
    projectProfileGateEvaluationNeeded,
    projectProfileGateDecision,
    profileProjectMatches,
    manualProjectProfileSelectionActive,
    profileApplySessionActive,
    projectProfileGateDefaultProfileKey,
    repairPersistenceRequiresConfirmation,
  };
}
